import { useState, useCallback } from 'react'
import {
  useUpdateJournal,
  useUpdateTask,
  useUpdateMovie,
  useUpdateBook,
  useUpdateArtist,
  useUpdateLabel,
  useUpdateRelease,
  useDeleteJournal,
  useDeleteTask,
  useDeleteMovie,
  useDeleteBook,
  useDeleteArtist,
  useDeleteLabel,
  useDeleteRelease,
} from '@/entries'
import type { FeedData } from './select-feed-sources'
import { toggleChecklistItem } from '@/entries'
import type { ChecklistItem, TaskDraft } from '@/entries'
import type { EntryWithPeople } from './types'

/**
 * Hook to manage all feed mutations (update, delete, complete)
 */
export function useFeedMutations() {
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [completingId, setCompletingId] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [checklistUpdates, setChecklistUpdates] = useState<
    Record<string, ChecklistItem[]>
  >({})

  // Update mutations
  const updateJournal = useUpdateJournal()
  const updateTask = useUpdateTask()
  const updateMovie = useUpdateMovie()
  const updateBook = useUpdateBook()
  const updateArtist = useUpdateArtist()
  const updateLabel = useUpdateLabel()
  const updateRelease = useUpdateRelease()

  // Delete mutations
  const deleteJournal = useDeleteJournal()
  const deleteTask = useDeleteTask()
  const deleteMovie = useDeleteMovie()
  const deleteBook = useDeleteBook()
  const deleteArtist = useDeleteArtist()
  const deleteLabel = useDeleteLabel()
  const deleteRelease = useDeleteRelease()

  // Journal handlers
  const handleDeleteJournal = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteJournal.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete journal:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteJournal],
  )

  const handleSaveJournal = useCallback(
    async (
      id: string,
      updates: {
        title?: string
        content?: string
        peopleIds?: string[]
        topicIds?: string[]
      },
    ) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateJournal.mutate(
          {
            id,
            data: {
              title: updates.title,
              content: updates.content,
              peopleIds: updates.peopleIds,
              topicIds: updates.topicIds,
            },
          },
          {
            onSuccess: () => {
              setSavingId(null)
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save journal:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateJournal],
  )

  // Task handlers
  const handleDeleteTask = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteTask.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete task:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteTask],
  )

  const handleCompleteTask = useCallback(
    (id: string) => {
      setCompletingId(id)
      // completedAt is automatically set by useUpdateTask when status changes to 'done'
      updateTask.mutate(
        { id, data: { status: 'done' } },
        {
          onSuccess: () => {
            setCompletingId(null)
          },
          onError: (error) => {
            console.error('Failed to complete task:', error)
            setCompletingId(null)
          },
        },
      )
    },
    [updateTask],
  )

  const handleSaveTask = useCallback(
    async (id: string, updates: Partial<TaskDraft>) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateTask.mutate(
          {
            id,
            data: updates,
          },
          {
            onSuccess: () => {
              setSavingId(null)
              // Clear any local checklist updates for this task since we just saved
              if (updates.checklist !== undefined) {
                setChecklistUpdates((prev) => {
                  const newState = { ...prev }
                  delete newState[id]
                  return newState
                })
              }
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save task:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateTask],
  )

  const handleChecklistToggle = useCallback(
    (
      taskId: string,
      itemId: string,
      checked: boolean,
      currentItems: ChecklistItem[],
    ) => {
      const updatedItems = toggleChecklistItem(currentItems, itemId, checked)
      setChecklistUpdates((prev) => ({ ...prev, [taskId]: updatedItems }))

      updateTask.mutate(
        { id: taskId, data: { checklist: updatedItems } },
        {
          onSuccess: () => {},
          onError: (error) => {
            setChecklistUpdates((prev) => {
              const newState = { ...prev }
              delete newState[taskId]
              return newState
            })
            console.error('Failed to update checklist:', error)
          },
        },
      )
    },
    [updateTask],
  )

  const getChecklistItems = useCallback(
    (task: EntryWithPeople): ChecklistItem[] | null => {
      return (
        checklistUpdates[task.id] ??
        (task.metadata.checklist as ChecklistItem[] | undefined) ??
        null
      )
    },
    [checklistUpdates],
  )

  // Movie handlers
  const handleDeleteMovie = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteMovie.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete movie:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteMovie],
  )

  const handleSaveMovie = useCallback(
    async (
      id: string,
      updates: {
        title?: string
        director?: string | null
        year?: number | null
        overview?: string | null
        genres?: string[]
        notes?: string | null
        status?: string
      },
    ) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateMovie.mutate(
          {
            id,
            data: {
              title: updates.title,
              director: updates.director,
              year: updates.year,
              overview: updates.overview,
              genres: updates.genres,
              notes: updates.notes,
              status: updates.status as 'want_to_watch' | 'seen',
            },
          },
          {
            onSuccess: () => {
              setSavingId(null)
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save movie:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateMovie],
  )

  // Book handlers
  const handleDeleteBook = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteBook.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete book:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteBook],
  )

  const handleSaveBook = useCallback(
    async (
      id: string,
      updates: {
        title?: string
        author?: string | null
        year?: number | null
        description?: string | null
        topics?: string[]
        notes?: string | null
        status?: string
      },
    ) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateBook.mutate(
          {
            id,
            data: {
              title: updates.title,
              author: updates.author,
              year: updates.year,
              description: updates.description,
              topics: updates.topics,
              notes: updates.notes,
              status: updates.status as 'want_to_read' | 'reading' | 'read',
            },
          },
          {
            onSuccess: () => {
              setSavingId(null)
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save book:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateBook],
  )

  // Artist handlers
  const handleDeleteArtist = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteArtist.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete artist:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteArtist],
  )

  const handleSaveArtist = useCallback(
    async (
      id: string,
      updates: {
        name?: string
        genres?: string[]
        bio?: string | null
        notes?: string | null
      },
    ) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateArtist.mutate(
          {
            id,
            data: {
              name: updates.name,
              genres: updates.genres,
              bio: updates.bio,
              notes: updates.notes,
            },
          },
          {
            onSuccess: () => {
              setSavingId(null)
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save artist:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateArtist],
  )

  // Label handlers
  const handleDeleteLabel = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteLabel.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete label:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteLabel],
  )

  const handleSaveLabel = useCallback(
    async (
      id: string,
      updates: {
        name?: string
        website?: string | null
        profile?: string | null
        notes?: string | null
        genres?: string[]
      },
    ) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateLabel.mutate(
          {
            id,
            data: {
              name: updates.name,
              website: updates.website,
              profile: updates.profile,
              notes: updates.notes,
              genres: updates.genres,
            },
          },
          {
            onSuccess: () => {
              setSavingId(null)
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save label:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateLabel],
  )

  // Release handlers
  const handleDeleteRelease = useCallback(
    (id: string) => {
      setDeletingId(id)
      deleteRelease.mutate(id, {
        onSuccess: () => {
          setDeletingId(null)
        },
        onError: (error) => {
          console.error('Failed to delete release:', error)
          setDeletingId(null)
        },
      })
    },
    [deleteRelease],
  )

  const handleSaveRelease = useCallback(
    async (
      id: string,
      updates: {
        title?: string
        year?: number | null
        genres?: string[]
        notes?: string | null
      },
    ) => {
      setSavingId(id)
      return new Promise<void>((resolve, reject) => {
        updateRelease.mutate(
          {
            id,
            data: {
              title: updates.title,
              year: updates.year,
              genres: updates.genres,
              notes: updates.notes,
            },
          },
          {
            onSuccess: () => {
              setSavingId(null)
              resolve()
            },
            onError: (error) => {
              console.error('Failed to save release:', error)
              setSavingId(null)
              reject(error)
            },
          },
        )
      })
    },
    [updateRelease],
  )

  return {
    // State
    deletingId,
    completingId,
    savingId,
    // Journal
    handleDeleteJournal,
    handleSaveJournal,
    // Task
    handleDeleteTask,
    handleCompleteTask,
    handleSaveTask,
    handleChecklistToggle,
    getChecklistItems,
    // Movie
    handleDeleteMovie,
    handleSaveMovie,
    // Book
    handleDeleteBook,
    handleSaveBook,
    // Artist
    handleDeleteArtist,
    handleSaveArtist,
    // Label
    handleDeleteLabel,
    handleSaveLabel,
    // Release
    handleDeleteRelease,
    handleSaveRelease,
  }
}
